import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import {
  colorV2,
  cssTemplate,
  media,
  typographyV2,
} from "../../../../../constants/styles";
import { ValueUpService } from "../../../../../constants/valueUpServices";
import { useIntersecting } from "../../../../../hooks/useIntersecting";
import Responsable from "../../../../Responsable";
import { ScreenType } from "../../../shared/types";

const ValueUpCard: React.FC<{
  service: ValueUpService;
  screenType: ScreenType;
  idx: number;
}> = ({ service, screenType }) => {
  const { ref, isIntersecting } = useIntersecting();
  const [interacted, setInteracted] = useState<boolean>(false);

  useEffect(() => {
    if (isIntersecting) setInteracted(true);
  }, [isIntersecting]);

  const getImageSrc = useCallback(
    (service: ValueUpService) => {
      switch (screenType) {
        case "TABLET":
          return service.tabletMainImg;
        case "MOBILE":
          return service.mobileMainImg;
        case "PC":
        default:
          return service.mainImg;
      }
    },
    [screenType]
  );

  const navigateToPage = (link: string) => {
    window.open(link, "_blank");
  };

  const renderViewText = (link: string) => {
    if (link === "end") return "서비스 종료";
    else if (link === "comming") return "서비스 준비중";
    else return "VISIT";
  };

  const navLink = (link: string) => {
    if (link === "end") return;
    else if (link === "comming") return;
    else navigateToPage(link);
  };

  const renderCardContent = (screenType: string) => {
    return (
      <CardContent>
        <CardContentDesc>
          <CardTitle2>
            <img src={service.logSrc || "/image/webp/icons/Milk.png"} />

            <div>{service.title}</div>
          </CardTitle2>
          <CardMainText>
            {screenType === "mobile" && service.mobileMainDesc
              ? service.mobileMainDesc
              : service.mainDesc}
          </CardMainText>
          <CardDateText>{service.date}</CardDateText>
          <CardSubText>
            {screenType === "pc"
              ? service.subDesc
              : screenType === "tablet"
                ? service.tabletSubDesc
                : service.mobileSubDesc}
          </CardSubText>
        </CardContentDesc>
        {<CardImgWrapper2
          onClick={() => service.link && navLink(service.link)}
          url={getImageSrc(service)}
          isLink={service.link}
          hoverDisabled={service.link === "end" || service.link === "comming"}
        >
          <div />
          {screenType === "pc" && (
            <div>
              <span>{service.link && renderViewText(service.link)}</span>
            </div>
          )}
        </CardImgWrapper2>}
      </CardContent>
    );
  };

  return (
    <CardWrapper ref={ref} theme={{ isIntersecting: interacted }}>
      <Responsable
        pc={<>{renderCardContent("pc")}</>}
        tablet={<>{renderCardContent("tablet")}</>}
        mobile={<>{renderCardContent("mobile")}</>}
      />
    </CardWrapper>
  );
};
export default ValueUpCard;

const CardContent = styled.div`
  text-align: left;
  justify-content: space-between;
  display: flex;
  gap: 40px;

  ${media.TABLET} {
    flex-wrap: wrap;
    justify-content: none;
    flex-direction: column;
  }

  ${media.MOBILE} {
    width: 100%;
    gap: 24px;
  }
`;

const CardContentDesc = styled.div`
  width: 580px;
  flex-shrink: 0;

  ${media.TABLET} {
    width: 100%;
  }
`;

const CardTitle2 = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 16px;
  ${typographyV2.HEADING_2_B};

  img {
    width: 60px;
    height: 60px;
  }

  ${media.MOBILE} {
    gap: 12px;
    padding-bottom: 12px;
    ${typographyV2.HEADING_5_B};

    img {
      width: 32px;
      height: 32px;
    }

    & > div:nth-child(1) {
      padding-right: 12px;
    }

    & > div:nth-child(1) img {
      width: 32px;
      height: 32px;
    }
  }
`;

const CardMainText = styled.p`
  ${typographyV2.HEADING_5_B};
  text-align: left;
  padding-bottom: 8px;

  ${media.MOBILE} {
    ${typographyV2.HEADING_9_B};
  }
`;

const CardDateText = styled.div`
  ${typographyV2.BODY_8_M};
  text-align: left;
  padding-bottom: 42px;

  ${media.MOBILE} {
    padding-bottom: 32px;
  }
`;

const CardSubText = styled.div`
  text-align: left;
  ${typographyV2.BODY_6_M};
  color: ${colorV2.GRAY_04};
  padding-bottom: 24px;

  ${media.MOBILE} {
    padding-bottom: 16px;
  }
`;


const CardImgWrapper2 = styled.div<{ url?: string; isLink?: string; hoverDisabled?: boolean }>`
  position: relative;
  max-width: 580px;
  width: 100%;
  height: 344px;
  overflow: hidden;
  border-radius: 32px;
  color: ${colorV2.GRAY_10};

  &:hover {
    ${({ isLink, hoverDisabled }) =>
    isLink &&
    !hoverDisabled &&
    css`
        cursor: pointer;
        & > div:nth-child(1) {
          transform: scale(1.2);
          transition: all 0.3s;
          ${media.TABLET} {
            transform: none;
          }
        }
        & > div:nth-child(2) {
          display: flex;
          background-color: rgba(17, 17, 17, 0.4);
          & > span {
            opacity: 1;
            cursor: pointer;
            display: block;
          }
          transition: all 0.3s;
        }
      `}
  }

  & > div:nth-child(1) {
    /* animation: slide 0.3s; */
    /* FIXME animation !!!!! // TODO  */
    @keyframes slide {
      from {
        transform: translateY(35px);
      }
      to {
        transform: translateY(0px);
      }
    }
    transform: scale(1);
    background-image: url(${({ url }) => url});
    background-size: cover;
    height: 100%;
    transition: all 0.3s;
    background-repeat: no-repeat;
    background-position: center;
  }

  & > div:nth-child(2) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(17, 17, 17, 0);
    display: flex;
    & > span {
      display: none;
      font-family: "Fivo Sans Modern";
      font-style: italic;
      font-weight: 700;
      font-size: 20px;
      line-height: 24px;
      vertical-align: middle;
      opacity: 0;
    }

    transition: all 0.3s;
    justify-content: center;
    align-items: center;
    align-content: center;
  }

  ${media.TABLET} {
    max-width: none;
    min-width: 327px;
    width: 100%;
    height: 100%;
    margin-left: 0;
    & > div:nth-child(1) {
      /* animation: slide 0.3s; */
      @keyframes slide {
        from {
          transform: translateY(35px);
        }
        to {
          transform: translateY(0px);
        }
      }

      transition: all 0.3s;
      background-image: url(${({ url }) => url});
      background-size: cover;
      height: 400px;
      background-position: center;
      background-repeat: no-repeat;
    }
    & > div:nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 400px;

      & > span {
        font-family: "Fivo Sans Modern";
        font-style: italic;
        font-weight: 700;
        font-size: 20px;
        line-height: 24px;
        cursor: pointer;
        vertical-align: middle;
      }

      transition: all 0.3s;
      justify-content: center;
      align-items: center;
      align-content: center;
    }
  }
  ${media.MOBILE} {
    min-width: 100px;
    width: 100%;
    height: 194px;
    & > div:nth-child(1) {
      /* animation: slide 0.3s; */
      @keyframes slide {
        from {
          transform: translateY(35px);
        }
        to {
          transform: translateY(0px);
        }
      }

      background-image: url(${({ url }) => url});
      background-size: cover;
      width: 100%;
      height: 194px;
      background-position: center;
      transition: all 0.3s;
      background-repeat: no-repeat;
    }
    & > div:nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 194px;

      & > span {
        font-family: "Fivo Sans Modern";
        font-style: italic;
        font-weight: 700;
        font-size: 20px;
        line-height: 24px;
        cursor: pointer;
        vertical-align: middle;
      }

      transition: all 0.3s linear;
      justify-content: center;
      align-items: center;
      align-content: center;
    }
  }
`;

const CardWrapper = styled.div`
  width: 100%;
  & > div.PC {
    display: flex;
    flex-direction: column;
    align-items: space-between;
    padding: 0px;
    gap: 40px;

    width: 100%;
    justify-content: center;
    top: 0;
    ${(props) => cssTemplate.fadeInOut(props)}
  }
  ${media.TABLET} {
    & > div.PC {
      display: none;
    }
    & > div.MOBILE {
      display: none;
    }
    & > div.TABLET {
      display: flex;
      flex-direction: column;
      align-items: space-between;
      padding: 0px;
      gap: 32px;
      justify-content: column-reverse;
      top: 0;
      ${(props) => cssTemplate.fadeInOut(props)}
    }
  }
  ${media.MOBILE} {
    display: flex;
    justify-content: center;
    & > div.PC {
      display: none;
    }
    & > div.TABLET {
      display: none;
    }
    & > div.MOBILE {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0px;
      justify-content: center;
      top: 0;
      ${(props) => cssTemplate.fadeInOut(props)}
    }
  }
`;
